import {
  AccountIdentifiers,
  CronoGatewayLinkedinAction,
  ListLead,
  ProspectIdentifiers,
} from 'crono-fe-common/types/crono-extension/linkedin';
import { CronoGatewayBackgroundAction } from 'crono-fe-common/types/crono-extension/background-script';
import { CronoGatewayCronoAction } from 'crono-fe-common/types/crono-extension/crono';
import { compareVersions } from 'crono-fe-common/utils';
import { CronoGatewayGoogleAction } from 'crono-fe-common/types/crono-extension/google';
import {
  CronoGatewayG2Action,
  G2Lead,
} from 'crono-fe-common/types/crono-extension/g2';

export type CronoGatewayAction =
  | CronoGatewayLinkedinAction
  | CronoGatewayCronoAction
  | CronoGatewayBackgroundAction
  | CronoGatewayGoogleAction
  | CronoGatewayG2Action;

export interface CronoGatewayResponse<T extends CronoGatewayAction> {
  status:
    | 'callSuccess'
    | 'unknownMethod'
    | 'invalidParams'
    | 'error'
    | 'authError'
    | 'tooManyRequests'
    | 'profileError';
  result?: T['result'];
  reason?: string;
  /**
   * Link the caller can offer alongside the failure — a CRM record or a Crono page the API named
   * in the error it returned. Errors cross the extension boundary as a plain string, so without
   * this the URL is lost between the background script and whatever renders the message.
   */
  reasonUrl?: string;
}

/**
 * Error carrying the {@link CronoGatewayResponse.reasonUrl} of the failure it was built from.
 */
export class CronoGatewayError extends Error {
  public readonly url?: string;

  constructor(message: string, url?: string) {
    super(message);
    this.name = 'CronoGatewayError';
    this.url = url;
  }
}

export function getCronoGatewayErrorUrl(error: unknown): string | null {
  return error instanceof CronoGatewayError && error.url ? error.url : null;
}

export type CronoGatewayParams<T extends CronoGatewayAction> =
  | CronoGatewayParamsProspect<T>
  | CronoGatewayParamsAccount<T>
  | T['params'];

export type CronoGatewayParamsProspect<T extends CronoGatewayAction> =
  T['params'] & ProspectIdentifiers;

export type CronoGatewayParamsAccount<T extends CronoGatewayAction> =
  T['params'] & AccountIdentifiers;

export const requiredExtensionVersionByActionTargetAndMethodName: Partial<
  Record<
    `${CronoGatewayAction['target']}:${CronoGatewayAction['methodName']}`,
    string
  >
> = {
  'linkedin:getAllInvitations': '1.3.14',
  'background-script:getExtensionVersion': '1.3.15',
  'linkedin:getPublicIdFromLeadId': '2.0.3',
  'linkedin:getCompanyNumericId': '6.9.7',
};

export const latestKnownExtensionVersion = Object.values(
  requiredExtensionVersionByActionTargetAndMethodName,
)
  .sort(compareVersions)
  .reverse()[0];

export type ImportableLead = ListLead | G2Lead;
